import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Helpers from '../../../../../utils/helper';
import EditableTable from '../../../../../components/EditableTable/EditableTable';
import {
    GridColDef,
    GridEditDateCell,
    GridEditSingleSelectCell,
    GridPreProcessEditCellProps,
    GridRowId,
    GridValidRowModel,
    GridValueOptionsParams,
    useGridApiRef,
} from '@mui/x-data-grid';
import TextInputColumn from '../../../../../components/EditableTable/CustomColumns/TextInputColumn/TextInputColumn';
import {
    removeErrorClassFromCellContent,
    validateConditionValueCell,
    validateFormList,
    validateFormType,
    validateGridEditorRowDisplayOrder,
    validateRequiredCell,
    validateUniqueItemName,
} from '../../../../../components/EditableTable/EditableTableValidationHelper';
import {
    DataTableColumn,
    VisibilityConditionValue,
} from '../../../../../models/DataRequestHub/DataTable';
import {
    AnswerTypeVariable,
    AnswerTypeVariableNamesList,
    FormTypeVariable,
    FormTypeVariableNamesList,
    NumericFormatVariableNamesList,
    ProjectVariable,
    ProjectVariableOption,
    YesNoDropdownOptionsList,
} from '../../../../../models/DataRequestHub/ProjectVariable';
import { EditorTypeEnum } from '../../../../../models/DataRequestHub/ProjectEditorEnum';
import DisplayLogicSwitcherColumn from '../../../../../components/EditableTable/CustomColumns/DisplayLogicSwitcherColumn';
import { NumericCell } from '../../../../DataRequest/DataTableForm/NumericCell/NumericCell';
import { Box } from '@mui/material';
import './DataTableEditor.scss';
import moment from 'moment';
import { TimeZone, TimeZoneHelper } from '../../../../../utils/timeZoneHelper';
import {
    MultiSelectEditCell,
    MultiSelectOption,
} from '../../../../../components/EditableTable/CustomColumns/MultiselectInputColumn/MultiselectInputColumn';
import { ConditionValue } from '../../../../../models/DataRequestHub/ItemDisplayCondition';
import PortalTooltip from '../../../../../components/PortalTooltip/PortalTooltip';
import CustomRenderCell from '../../../../../components/EditableTable/CustomRenderCell/CustomRenderCell';
import VisibilityConditionSwitcherColumn from '../../../../../components/EditableTable/CustomColumns/VisibilityConditionSwitcherColumn';

interface DataTableEditorInterface {
    tableColumns: DataTableColumn[];
    setTableColumns(rows: DataTableColumn[]): void;
    isLoading: boolean;
    answerType: ProjectVariable;
    formType: ProjectVariable;
    numericFormat: ProjectVariable;
    dataNdaTerms: ProjectVariable;
    notSystemVariables: ProjectVariable[];
    fileConfigurationVariable: ProjectVariable;
    setIsProjectVariableEditPopupVisible(value: boolean): void;
    setVarialbeIdToEdit(value: number): void;
    setVariableNameToEdit(value: string): void;
    setProjectVariableOptionsToEdit(value: Array<ProjectVariableOption>): void;
}

const DataTableEditor = forwardRef((props: DataTableEditorInterface, ref) => {
    const apiRef = useGridApiRef();
    const {
        tableColumns: tableColumns,
        setTableColumns: setTableColumns,
        isLoading,
        answerType,
        formType,
        numericFormat,
        dataNdaTerms,
        notSystemVariables,
        fileConfigurationVariable,
        setIsProjectVariableEditPopupVisible,
        setVarialbeIdToEdit,
        setVariableNameToEdit,
        setProjectVariableOptionsToEdit,
    } = props;

    const gridTableRef = useRef(null);
    useImperativeHandle(ref, () => ({
        addRow() {
            gridTableRef.current.addRow();
        },
        validateAllRows() {
            return validateAllRows();
        },
        getEditorState() {
            return apiRef.current.state;
        },
    }));

    const getVisibilityConditionValueOptions =
        (): VisibilityConditionValue[] => {
            const variableOptions = dataNdaTerms?.options.map((option) => ({
                data: option.option,
                id: option.id,
            }));
            return variableOptions;
        };

    const getConditionValueOptions = (
        currentColumn: DataTableColumn
    ): string[] => {
        const parentColumn = getColumnGridModelByCusomtId(
            currentColumn.logicParentCustomId
        );

        const formType = FormTypeVariableNamesList.find(
            (f) => f.name === parentColumn?.formType
        );

        if (formType?.id === FormTypeVariable.YesNo) {
            return YesNoDropdownOptionsList.map((m) => m.name).reverse();
        }

        if (
            formType?.id === FormTypeVariable.DropDown &&
            parentColumn?.formList
        ) {
            const variableOptions =
                notSystemVariables.find(
                    (variable) => variable.name === parentColumn?.formList
                )?.options ?? [];
            return variableOptions?.map((option) => option.option);
        }

        return [];
    };

    const getDefaultValueOptions = (
        currentColumn: DataTableColumn
    ): string[] => {
        const formType = FormTypeVariableNamesList.find(
            (f) => f.name === currentColumn?.formType
        );

        if (formType?.id === FormTypeVariable.YesNo) {
            return YesNoDropdownOptionsList.map((m) => m.name).reverse();
        }

        if (
            formType?.id === FormTypeVariable.DropDown &&
            currentColumn?.formList
        ) {
            const variableOptions =
                notSystemVariables.find(
                    (variable) => variable.name === currentColumn?.formList
                )?.options ?? [];
            return variableOptions?.map((option) => option.option);
        }

        return [];
    };

    const getColumnGridModelByCusomtId = (
        customColumnId: string
    ): DataTableColumn => {
        const latestStateMap = apiRef.current.getRowModels();
        const latestStateArray = Array.from(
            latestStateMap.values()
        ) as DataTableColumn[];
        return latestStateArray?.find(
            (questionModel) => questionModel.customColumnId === customColumnId
        );
    };

    const getParentColumnIdOptions = (currentColumn: DataTableColumn) => {
        const fileTypeName = AnswerTypeVariableNamesList.find(
            (f) => f.id === AnswerTypeVariable.File
        )?.name;

        const parentCustomIds = props.tableColumns
            .filter((col) => col.answerType !== fileTypeName)
            .map((col) => col.customColumnId);

        const selfIndex = parentCustomIds.indexOf(currentColumn.customColumnId);
        if (selfIndex !== -1) parentCustomIds.splice(selfIndex, 1);

        return parentCustomIds;
    };

    const updateRows = (
        newRows: (oldColumns: DataTableColumn[]) => DataTableColumn[]
    ): void => {
        const result = newRows(tableColumns);
        setTableColumns(result);
    };

    const getColumnByCustomId = (customColumnId: string) => {
        return props.tableColumns.find(
            (column) => column.customColumnId === customColumnId
        );
    };

    const getRowId = (row: GridValidRowModel) => {
        return row.customColumnId;
    };

    const displayLogicSwitcherColumn = DisplayLogicSwitcherColumn({
        setRows: updateRows,
        gridApiRef: apiRef,
        getRowId: getRowId,
    });

    const visibilityConditionSwitcherColumn = VisibilityConditionSwitcherColumn(
        {
            setRows: updateRows,
            gridApiRef: apiRef,
            getRowId: getRowId,
        }
    );

    const isParentAnswerDescribed = (tableColumn: DataTableColumn) => {
        const parentColumn = getColumnByCustomId(
            tableColumn?.logicParentCustomId
        );

        if (!parentColumn) {
            return false;
        }

        const definedTypes = AnswerTypeVariableNamesList.filter(
            (f) =>
                f.id === AnswerTypeVariable.Date ||
                f.id === AnswerTypeVariable.Text
        );

        const definedFormOptions = [
            ...FormTypeVariableNamesList.filter(
                (f) => f.id === FormTypeVariable.YesNo
            ),
            ...NumericFormatVariableNamesList,
        ].map((definedFormTypeOption) => definedFormTypeOption.name);

        const isParentColumnAnswerIdentified =
            parentColumn?.formList ||
            definedFormOptions.some(
                (formOption) => formOption === parentColumn?.formType
            ) ||
            definedTypes.some((type) => type.name === parentColumn?.answerType);

        return isParentColumnAnswerIdentified;
    };

    const handleConditionValueDropdownChange = async (
        newValue: string,
        oldRowId: GridRowId
    ) => {
        await apiRef.current.setEditCellValue({
            id: oldRowId,
            field: 'conditionValues',
            value: newValue,
        });
    };

    const handleDefaultValueDropdownChange = async (
        newValue: string,
        oldRowId: GridRowId
    ) => {
        await apiRef.current.setEditCellValue({
            id: oldRowId,
            field: 'defaultValue',
            value: newValue,
        });
    };

    const resetCellsDependedOnTargetColumnId = (customColumnId: string) => {
        const latestStateMap = apiRef.current.getRowModels();
        const latestStateArray = Array.from(latestStateMap.values());

        const updatedStateArray = latestStateArray.map((row) => {
            const question = row as DataTableColumn;
            if (question.logicParentCustomId === customColumnId) {
                return {
                    ...row,
                    conditionValues: [],
                };
            }
            if (question.customColumnId === customColumnId) {
                return {
                    ...row,
                    defaultValue: '',
                };
            }
            return row;
        });

        updateRows(() => updatedStateArray as DataTableColumn[]);
    };

    const handleParentColumnAnswerTypeChange = async (
        newValue: string,
        field: string,
        currentRowId: GridRowId,
        current: DataTableColumn
    ) => {
        await apiRef.current.setEditCellValue({
            id: currentRowId,
            field: field,
            value: newValue,
        });

        resetCellsDependedOnTargetColumnId(current.customColumnId);
    };

    const getConditionalityValueOptions = (
        currentColumn: DataTableColumn
    ): MultiSelectOption[] => {
        const variableOptions =
            notSystemVariables.find(
                (variable) => variable.name === currentColumn?.formList
            )?.options ?? [];
        return variableOptions?.map((option, index) => ({
            data: option.option,
            id: index,
        }));
    };

    const handleParentColumnIdChange = (customColumnId: string) => {
        const latestStateMap = apiRef.current.getRowModels();
        const latestStateArray = Array.from(latestStateMap.values());

        const updatedStateArray = latestStateArray.map((row) => {
            const question = row as DataTableColumn;
            if (question.customColumnId === customColumnId) {
                return {
                    ...row,
                    conditionValues: [],
                };
            }

            return row;
        });

        updateRows(() => updatedStateArray as DataTableColumn[]);
    };

    const handleVisibilityConditionVariableChange = (
        customColumnId: string
    ) => {
        const latestStateMap = apiRef.current.getRowModels();
        const latestStateArray = Array.from(latestStateMap.values());

        const updatedStateArray = latestStateArray.map((row) => {
            const question = row as DataTableColumn;
            if (question.customColumnId === customColumnId) {
                return {
                    ...row,
                    visibilityConditionValues: [],
                };
            }

            return row;
        });

        updateRows(() => updatedStateArray as DataTableColumn[]);
    };

    const getCellTooltip = (anchorId: string, content: React.JSX.Element) => {
        return (
            <PortalTooltip
                anchorId={anchorId}
                delayShow={1000}
                place="bottom"
                content={content}
            />
        );
    };

    const columns = (): GridColDef[] => [
        {
            field: 'displayOrder',
            headerName: 'Display Order',
            minWidth: 140,
            flex: 0.25,
            type: 'number',
            cellClassName: (params) => {
                let cellClass = 'cell-input display-order';

                if (params.row.isSubQuestion) {
                    cellClass = `${cellClass} disabled`;
                }

                return cellClass;
            },
            editable: true,
            align: 'left',
            headerAlign: 'left',
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const value = params.props.value;
                const hasError = value <= 0;
                return { ...params.props, error: hasError };
            },
            renderCell: (params) => {
                const displayOrderCellId = `display-order-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={displayOrderCellId}
                        renderValue={params.value}
                    />
                );
            },
        },
        {
            field: 'customColumnId',
            headerName: 'C-ID',
            minWidth: 150,
            flex: 0.5,
            cellClassName: 'cell-text-input custom-column-id',
            editable: false,
            renderCell: (params) => {
                const customColumnIdCellId = `custom-column-id-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={customColumnIdCellId}
                        renderValue={params.value}
                    />
                );
            },
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                return { ...params.props };
            },
            renderEditCell: (params) => <TextInputColumn {...params} />,
        },
        {
            field: 'name',
            headerName: 'Column Name',
            minWidth: 150,
            flex: 0.5,
            cellClassName: 'cell-text-input display-name',
            editable: true,
            renderCell: (params) => {
                const nameCellId = `name-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={nameCellId}
                        renderValue={params.value}
                    />
                );
            },
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const value = params.props.value;

                const hasError = value?.trim().length <= 0;
                return { ...params.props, error: hasError };
            },
            renderEditCell: (params) => <TextInputColumn {...params} />,
        },
        {
            field: 'columnTooltip',
            headerName: 'Column Tooltip',
            minWidth: 150,
            flex: 0.5,
            cellClassName: 'cell-text-input display-name',
            editable: true,
            renderCell: (params) => {
                const columnTooltipCellId = `tooltip-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={columnTooltipCellId}
                        renderValue={params.value}
                    />
                );
            },
            renderEditCell: (params) => <TextInputColumn {...params} />,
        },
        {
            field: 'answerType',
            headerName: 'Answer Type',
            minWidth: 170,
            cellClassName: 'answer-type',
            flex: 0.5,
            type: 'singleSelect',
            valueOptions: answerType?.options.map((x) => x.option),
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = params.props.value?.length <= 0;
                return { ...params.props, error: hasError };
            },
            renderEditCell: (params) => (
                <GridEditSingleSelectCell
                    onValueChange={(_, newValue) =>
                        handleParentColumnAnswerTypeChange(
                            newValue,
                            params.field,
                            params.id,
                            params.row
                        )
                    }
                    {...params}
                />
            ),
            renderCell: (params) => {
                const answerTypeIdCellId = `answer-type-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={answerTypeIdCellId}
                        renderValue={params.value}
                    />
                );
            },
        },
        {
            field: 'formType',
            headerName: 'Form Type',
            minWidth: 170,
            cellClassName: 'form-type',
            flex: 0.5,
            type: 'singleSelect',
            valueOptions: (params: GridValueOptionsParams<DataTableColumn>) => {
                switch (params.row.answerType) {
                    case AnswerTypeVariableNamesList.find(
                        (f) => f.id === AnswerTypeVariable.PickFromList
                    ).name:
                        return formType?.options.map((x) => x.option);
                    case AnswerTypeVariableNamesList.find(
                        (f) => f.id === AnswerTypeVariable.Number
                    ).name:
                        return numericFormat?.options.map((x) => x.option);
                }
            },
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = params.props.value?.length <= 0;
                return { ...params.props, error: hasError };
            },
            renderEditCell: (params) => (
                <GridEditSingleSelectCell
                    onValueChange={(_, newValue) =>
                        handleParentColumnAnswerTypeChange(
                            newValue,
                            params.field,
                            params.id,
                            params.row
                        )
                    }
                    {...params}
                />
            ),
            renderCell: (params) => {
                const formTypeCellId = `form-type-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={formTypeCellId}
                        renderValue={params.value}
                    />
                );
            },
        },
        {
            field: 'formList',
            headerName: 'Form List',
            minWidth: 170,
            cellClassName: 'form-list',
            flex: 0.5,
            type: 'singleSelect',
            valueOptions: notSystemVariables
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((x) => x.name),
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = params.props.value?.length <= 0;
                return { ...params.props, error: hasError };
            },
            renderEditCell: (params) => (
                <GridEditSingleSelectCell
                    onValueChange={(_, newValue) =>
                        handleParentColumnAnswerTypeChange(
                            newValue,
                            params.field,
                            params.id,
                            params.row
                        )
                    }
                    {...params}
                />
            ),
            renderCell: (params) => {
                const formListCellId = `form-list-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={formListCellId}
                        renderValue={params.value}
                    />
                );
            },
        },
        displayLogicSwitcherColumn,
        {
            field: 'logicParentCustomId',
            headerName: 'Conditional Column',
            minWidth: 150,
            flex: 0.5,
            cellClassName: (params) => {
                let cellClass = 'cell-text-input logic-parent-column-id';

                const isLogicEnabled = params.row.isDisplayLogicEnabled;
                if (!isLogicEnabled) {
                    cellClass = `${cellClass} disabled`;
                }

                return cellClass;
            },
            type: 'singleSelect',
            valueOptions: (params) => getParentColumnIdOptions(params.row),
            editable: true,
            renderEditCell: (params) => (
                <GridEditSingleSelectCell
                    onValueChange={() => {
                        handleParentColumnIdChange(params.row.customColumnId);
                    }}
                    {...params}
                />
            ),
            renderCell: (params) => {
                const logicParentCustomIdCellId = `logic-parent-custom-id-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={logicParentCustomIdCellId}
                        renderValue={params.value}
                    />
                );
            },
        },
        {
            field: 'conditionValues',
            headerName: 'Condition Value',
            type: 'singleSelect',
            valueOptions: (params) => getConditionValueOptions(params.row),
            minWidth: 140,
            flex: 0.25,
            valueSetter: (value, row) => {
                if (Object.prototype.toString.call(value) === '[object Date]') {
                    const currentValue = row.conditionValues;
                    const newDate = new Date(value);
                    if (
                        currentValue &&
                        newDate.toDateString() === currentValue
                    ) {
                        return row;
                    }

                    const isDateValid = TimeZoneHelper.isDateValid(newDate);
                    let dateOnly = '';

                    if (isDateValid) {
                        dateOnly = newDate.toDateString();
                    }
                    const newConditionValue = [
                        {
                            id: 0,
                            data: dateOnly,
                        },
                    ];

                    return {
                        ...row,
                        conditionValues: newConditionValue,
                    };
                }

                if (Array.isArray(value)) {
                    return {
                        ...row,
                        conditionValues: value,
                    };
                }

                return {
                    ...row,
                    conditionValues: [{ id: 0, data: value }],
                };
            },
            valueFormatter: (value: any, params) => {
                const currentColumn = params as DataTableColumn;
                const parentColumnDefinition = getColumnGridModelByCusomtId(
                    currentColumn?.logicParentCustomId
                );
                const parentFormType = FormTypeVariableNamesList.find(
                    (x) => x.name === parentColumnDefinition?.formType
                )?.id;
                const parentAnswerType = AnswerTypeVariableNamesList.find(
                    (x) => x.name === parentColumnDefinition?.answerType
                )?.id;

                if (
                    parentFormType !== FormTypeVariable.DropDown ||
                    parentAnswerType !== AnswerTypeVariable.PickFromList
                ) {
                    return Array.isArray(value) ? value[0]?.data : value;
                }

                if (!Array.isArray(value)) {
                    return [{ id: 0, data: value }];
                }

                return value;
            },
            cellClassName: (params) => {
                let cellClass = 'cell-text-input condition-value';

                const isLogicEnabled = params.row.isDisplayLogicEnabled;
                const hasParent = params.row.logicParentCustomId;
                const isParentAnswerIdentified = isParentAnswerDescribed(
                    params.row
                );
                if (
                    !isLogicEnabled ||
                    !hasParent ||
                    !isParentAnswerIdentified
                ) {
                    cellClass = `${cellClass} disabled`;
                }

                return cellClass;
            },
            editable: true,
            align: 'left',
            headerAlign: 'left',
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                if (
                    Object.prototype.toString.call(params.props.value) ===
                    '[object Date]'
                ) {
                    const hasDateError = !!!params.props.value;
                    return { ...params.props, error: hasDateError };
                }

                if (Array.isArray(params.props.value)) {
                    const conditionValues = params.props
                        .value as ConditionValue[];
                    const hasArrayError = conditionValues.some(
                        (item) => item.data?.trim().length <= 0
                    );
                    return { ...params.props, error: hasArrayError };
                }

                const valueLenght = params.props.value?.trim().lenght;
                const hasError = valueLenght <= 0;

                return { ...params.props, error: hasError };
            },
            renderEditCell: (params) => {
                const currentColumn = params.row as DataTableColumn;
                const parentColumnDefinition = getColumnGridModelByCusomtId(
                    currentColumn.logicParentCustomId
                );
                const parentAnswerType = AnswerTypeVariableNamesList.find(
                    (type) => type.name === parentColumnDefinition?.answerType
                )?.id;

                switch (parentAnswerType) {
                    case AnswerTypeVariable.Text:
                        return (
                            <TextInputColumn
                                {...params}
                                value={params.formattedValue}
                            />
                        );
                    case AnswerTypeVariable.Number:
                        return (
                            <div className="numeric-cell-edit">
                                <NumericCell
                                    {...params}
                                    isReadonly={false}
                                    formType={
                                        parentColumnDefinition.formType ?? ''
                                    }
                                    value={params.formattedValue}
                                />
                            </div>
                        );
                    case AnswerTypeVariable.PickFromList: {
                        const parentFormType = FormTypeVariableNamesList.find(
                            (x) => x.name === parentColumnDefinition.formType
                        )?.id;
                        if (parentFormType === FormTypeVariable.DropDown) {
                            return MultiSelectEditCell(
                                { ...params, value: params.formattedValue },
                                getConditionalityValueOptions(
                                    parentColumnDefinition
                                )
                            );
                        }

                        return (
                            <GridEditSingleSelectCell
                                onValueChange={(_, newValue) => {
                                    handleConditionValueDropdownChange(
                                        newValue,
                                        params.id
                                    );
                                }}
                                {...params}
                                value={params.formattedValue}
                            />
                        );
                    }
                    case AnswerTypeVariable.Date:
                        return (
                            <GridEditDateCell
                                className="cell-date-input"
                                {...params}
                                value={params.formattedValue}
                            />
                        );
                    default:
                        return <></>;
                }
            },
            renderCell: (params) => {
                const currentColumn = params.row as DataTableColumn;
                const parentColumnDefinition = getColumnGridModelByCusomtId(
                    currentColumn.logicParentCustomId
                );
                const parentAnswerType = AnswerTypeVariableNamesList.find(
                    (type) => type.name === parentColumnDefinition?.answerType
                )?.id;

                const conditionCellId = `form-condition-cell-${params.id}`;

                switch (parentAnswerType) {
                    case AnswerTypeVariable.Number:
                        const numberConditionTooltipContent = (
                            <NumericCell
                                isReadonly={true}
                                formType={currentColumn.formType}
                                {...params}
                                value={params.formattedValue}
                            />
                        );
                        return (
                            <Box className="MuiDataGrid-cellContent custom-render-cell">
                                {params.value
                                    ? getCellTooltip(
                                          conditionCellId,
                                          numberConditionTooltipContent
                                      )
                                    : []}
                                <NumericCell
                                    {...params}
                                    isReadonly={true}
                                    formType={parentColumnDefinition.formType}
                                    value={params.formattedValue}
                                    elementId={conditionCellId}
                                />
                            </Box>
                        );
                    case AnswerTypeVariable.Date:
                        const dateConditionTooltipContent = (
                            <div>
                                {moment(params?.formattedValue).format('L')}
                            </div>
                        );
                        return (
                            <Box className="MuiDataGrid-cellContent custom-render-cell">
                                {params.formattedValue
                                    ? getCellTooltip(
                                          conditionCellId,
                                          dateConditionTooltipContent
                                      )
                                    : []}
                                <div id={conditionCellId}>
                                    {params?.formattedValue
                                        ? moment(params?.formattedValue).format(
                                              'L'
                                          )
                                        : null}
                                </div>
                            </Box>
                        );
                    case AnswerTypeVariable.PickFromList:
                        const stringArray = Array.isArray(params.formattedValue)
                            ? params.formattedValue?.map(
                                  (condition: ConditionValue) => condition.data
                              )
                            : [params.formattedValue];

                        const displayValue = stringArray.join(', ');

                        return (
                            <CustomRenderCell
                                {...params}
                                anchorTooltipId={conditionCellId}
                                renderValue={displayValue}
                            />
                        );
                    default:
                        return (
                            <CustomRenderCell
                                {...params}
                                anchorTooltipId={conditionCellId}
                                renderValue={params.formattedValue}
                            />
                        );
                }
            },
        },
        {
            field: 'defaultValue',
            headerName: 'Default',
            type: 'singleSelect',
            valueOptions: (params) => getDefaultValueOptions(params.row),
            minWidth: 140,
            flex: 0.25,
            valueSetter: (value, row) => {
                if (Object.prototype.toString.call(value) === '[object Date]') {
                    const newDate = new Date(value);
                    const isDateValid = TimeZoneHelper.isDateValid(newDate);

                    let dateOnly = '';

                    if (isDateValid) {
                        dateOnly = newDate.toDateString();
                    }

                    return {
                        ...row,
                        defaultValue: dateOnly,
                    };
                }

                return {
                    ...row,
                    defaultValue: value,
                };
            },
            cellClassName: (params) => {
                let cellClass = 'cell-text-input default-value';
                const tableColumn = props.tableColumns.find(
                    (column) =>
                        column.customColumnId === params.row.customColumnId
                );
                const definedTypes = AnswerTypeVariableNamesList.filter(
                    (f) =>
                        f.id === AnswerTypeVariable.Date ||
                        f.id === AnswerTypeVariable.Text
                );

                const definedFormOptions = [
                    ...FormTypeVariableNamesList.filter(
                        (f) => f.id === FormTypeVariable.YesNo
                    ),
                    ...NumericFormatVariableNamesList,
                ].map((definedFormTypeOption) => definedFormTypeOption.name);

                const isParentAnswerIdentified =
                    (tableColumn?.formList != null &&
                        tableColumn?.formList != '') ||
                    definedFormOptions.some(
                        (formOption) => formOption === tableColumn?.formType
                    ) ||
                    definedTypes.some(
                        (type) => type.name === tableColumn?.answerType
                    );

                if (!isParentAnswerIdentified) {
                    cellClass = `${cellClass} disabled`;
                }

                return cellClass;
            },
            editable: true,
            align: 'left',
            headerAlign: 'left',
            renderEditCell: (params) => {
                const currentColumn = params.row as DataTableColumn;
                const answerType = AnswerTypeVariableNamesList.find(
                    (type) => type.name === currentColumn?.answerType
                )?.id;

                switch (answerType) {
                    case AnswerTypeVariable.Text:
                        return <TextInputColumn {...params} />;
                    case AnswerTypeVariable.Number:
                        return (
                            <div className="numeric-cell-edit">
                                <NumericCell
                                    isReadonly={false}
                                    formType={currentColumn.formType}
                                    {...params}
                                />
                            </div>
                        );
                    case AnswerTypeVariable.PickFromList:
                        return (
                            <GridEditSingleSelectCell
                                onValueChange={(_, newValue) => {
                                    handleDefaultValueDropdownChange(
                                        newValue,
                                        params.id
                                    );
                                }}
                                {...params}
                            />
                        );
                    case AnswerTypeVariable.Date:
                        return (
                            <GridEditDateCell
                                className="cell-date-input"
                                {...params}
                            />
                        );
                    default:
                        return <></>;
                }
            },
            renderCell: (params) => {
                const currentColumn = params.row as DataTableColumn;
                const answerType = AnswerTypeVariableNamesList.find(
                    (type) => type.name === currentColumn?.answerType
                )?.id;

                const defaultCellId = `default-cell-${params.id}`;

                switch (answerType) {
                    case AnswerTypeVariable.Number:
                        const numberConditionTooltipContent = (
                            <NumericCell
                                isReadonly={true}
                                formType={currentColumn.formType}
                                {...params}
                            />
                        );
                        return (
                            <Box className="MuiDataGrid-cellContent custom-render-cell">
                                {params.value
                                    ? getCellTooltip(
                                          defaultCellId,
                                          numberConditionTooltipContent
                                      )
                                    : []}
                                <NumericCell
                                    isReadonly={true}
                                    formType={currentColumn.formType}
                                    {...params}
                                    elementId={defaultCellId}
                                />
                            </Box>
                        );
                    case AnswerTypeVariable.Date:
                        const dateConditionTooltipContent = (
                            <div>{moment(params?.value).format('L')}</div>
                        );

                        return (
                            <Box className="MuiDataGrid-cellContent custom-render-cell">
                                {params.value
                                    ? getCellTooltip(
                                          defaultCellId,
                                          dateConditionTooltipContent
                                      )
                                    : []}
                                <div id={defaultCellId}>
                                    {params?.value
                                        ? moment(params?.value).format('L')
                                        : null}
                                </div>
                            </Box>
                        );
                    default:
                        return (
                            <CustomRenderCell
                                {...params}
                                anchorTooltipId={defaultCellId}
                                renderValue={params.value}
                            />
                        );
                }
            },
        },
        {
            field: 'storageFolder',
            headerName: 'Storage Folder',
            cellClassName: (params) => {
                const isFile = params.row.answerType === AnswerTypeVariableNamesList.find(
                    (f) => f.id === AnswerTypeVariable.File
                )?.name;
                return `cell-text-input storage-folder${isFile ? '' : ' disabled'}`;
            },
            minWidth: 170,
            flex: 0.5,
            type: 'singleSelect',
            renderHeader: () => (
                <>
                    Storage Folder
                    <FontAwesomeIcon
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsProjectVariableEditPopupVisible(true);
                            setVarialbeIdToEdit(fileConfigurationVariable.id);
                            setVariableNameToEdit(fileConfigurationVariable.name);
                            setProjectVariableOptionsToEdit(fileConfigurationVariable.options);
                        }}
                        style={{ marginLeft: '10px' }}
                        size="1x"
                        icon={faEdit as any}
                        title="Edit"
                    />
                </>
            ),
            renderCell: (params) => {
                const storageFolderCellId = `storage-folder-cell-${params.id}`;
                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={storageFolderCellId}
                        renderValue={params.value}
                    />
                );
            },
            valueOptions: fileConfigurationVariable?.options.map((x) => x.option),
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                if (params.hasChanged) {
                    const value = params.props.value as string;
                    const hasError = Helpers.hasFoldersPathError(value, '/');
                    return { ...params.props, error: hasError };
                }
                return { ...params.props };
            },
        },
        visibilityConditionSwitcherColumn,
        {
            field: 'visibilityConditionVariable',
            headerName: 'Variable',
            minWidth: 150,
            flex: 0.5,
            cellClassName: (params) => {
                let cellClass = 'cell-text-input visibility-condition-variable';

                const isLogicEnabled = params.row.isVisibilityConditionEnabled;
                if (!isLogicEnabled) {
                    cellClass = `${cellClass} disabled`;
                }

                return cellClass;
            },
            type: 'singleSelect',
            valueOptions: [dataNdaTerms].map((x) => x.name),
            editable: true,
            renderEditCell: (params) => (
                <GridEditSingleSelectCell
                    onValueChange={() => {
                        handleVisibilityConditionVariableChange(
                            params.row.customColumnId
                        );
                    }}
                    {...params}
                />
            ),
            renderCell: (params) => {
                const visibilityConditionVariable = `visibility-condition-variable-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={visibilityConditionVariable}
                        renderValue={params.value}
                    />
                );
            },
        },
        {
            field: 'visibilityConditionValues',
            headerName: 'Value',
            minWidth: 150,
            flex: 0.5,
            cellClassName: (params) => {
                let cellClass = 'cell-text-input visibility-condition-values';

                const isLogicEnabled = params.row.isVisibilityConditionEnabled;
                const hasConditionVariable =
                    params.row.visibilityConditionVariable;
                if (!isLogicEnabled || !hasConditionVariable) {
                    cellClass = `${cellClass} disabled`;
                }

                return cellClass;
            },
            type: 'singleSelect',
            valueOptions: () => getVisibilityConditionValueOptions(),
            valueSetter: (value, row) => {
                if (Array.isArray(value)) {
                    return {
                        ...row,
                        visibilityConditionValues: value,
                    };
                }

                return {
                    ...row,
                    visibilityConditionValues: [{ id: 0, data: value }],
                };
            },
            valueFormatter: (value: VisibilityConditionValue[]) => {
                const data = value?.map((x) => x.data);
                return data
                    ? data
                          .sort((a, b) => {
                              return a > b ? -1 : 1;
                          })
                          .join(', ')
                    : '';
            },
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const valueLenght = params.props.value?.lenght;
                const hasError = valueLenght <= 0;
                return { ...params.props, error: hasError };
            },
            renderEditCell: (params) =>
                MultiSelectEditCell(
                    params,
                    getVisibilityConditionValueOptions()
                ),
            renderCell: (params) => {
                const visibilityConditionValuesCellId = `visibility-condition-values-cell-${params.id}`;

                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={visibilityConditionValuesCellId}
                        renderValue={params.formattedValue}
                    />
                );
            },
        },
    ];

    const runValidation = (
        column: DataTableColumn,
        element: Element
    ): boolean => {
        let isValid = true;
        const displayNameElement = element.querySelector('.display-name');
        const answerTypeElement = element.querySelector('.answer-type');
        const logicParentIdElement = element.querySelector(
            '.logic-parent-column-id'
        );
        const conditionValueElement = element.querySelector('.condition-value');
        const visibilityConditionVariableElement = element.querySelector(
            '.visibility-condition-variable'
        );
        const visibilityConditionValueElement = element.querySelector(
            '.visibility-condition-values'
        );

        if (validateRequiredCell(displayNameElement, column.name)) {
            isValid = false;
        }
        if (validateRequiredCell(answerTypeElement, column.answerType)) {
            isValid = false;
        }

        if (column.isDisplayLogicEnabled) {
            if (
                validateRequiredCell(
                    logicParentIdElement,
                    column.logicParentCustomId
                )
            ) {
                isValid = false;
            }

            if (column.logicParentCustomId && isParentAnswerDescribed(column)) {
                const validationResult = validateConditionValueCell(
                    element,
                    '.condition-value',
                    column.conditionValues
                );
                isValid = validationResult ? false : isValid;
            } else {
                removeErrorClassFromCellContent(conditionValueElement);
            }
        } else {
            removeErrorClassFromCellContent(logicParentIdElement);
            removeErrorClassFromCellContent(conditionValueElement);
        }

        if (column.isVisibilityConditionEnabled) {
            if (
                validateRequiredCell(
                    visibilityConditionVariableElement,
                    column.visibilityConditionVariable
                )
            ) {
                isValid = false;
            }

            if (column.visibilityConditionVariable) {
                if (
                    validateRequiredCell(
                        visibilityConditionValueElement,
                        column.visibilityConditionValues
                    )
                ) {
                    isValid = false;
                }
            } else {
                removeErrorClassFromCellContent(
                    visibilityConditionValueElement
                );
            }
        } else {
            removeErrorClassFromCellContent(visibilityConditionVariableElement);
            removeErrorClassFromCellContent(visibilityConditionValueElement);
        }

        return isValid;
    };

    const validateAllRows = () => {
        let isValid = true;
        for (let index = 0; index < tableColumns.length; index++) {
            const rowId = tableColumns[index].customColumnId;
            const element = document.querySelector('[data-id="' + rowId + '"]');
            const column = tableColumns[index];
            if (element) {
                const isColumnValid = runValidation(column, element);
                if (!isColumnValid) {
                    isValid = false;
                }
            }
        }

        if (!apiRef.current) {
            return isValid;
        }

        apiRef.current.forceUpdate();
        if (
            !validateGridEditorRowDisplayOrder(
                tableColumns,
                apiRef?.current?.state,
                EditorTypeEnum.DataTable
            )
        ) {
            isValid = false;
        }

        const mappedDisplayNameColumns = tableColumns.map((m) => ({
            ...m,
            displayName: m.name,
        }));
        if (
            !validateUniqueItemName(
                mappedDisplayNameColumns,
                apiRef.current.state,
                EditorTypeEnum.DataTable
            )
        ) {
            isValid = false;
        }
        if (
            !validateFormType(
                tableColumns,
                apiRef.current.state,
                EditorTypeEnum.DataTable
            )
        ) {
            isValid = false;
        }

        if (
            !validateFormList(
                tableColumns,
                apiRef.current.state,
                EditorTypeEnum.DataTable
            )
        ) {
            isValid = false;
        }

        return isValid;
    };

    useEffect(() => {
        validateAllRows();
    }, [tableColumns]);

    return (
        <div className="data-table-editor">
            <EditableTable
                editorType={EditorTypeEnum.DataTable}
                columns={columns()}
                rows={tableColumns}
                setRows={updateRows}
                isLoading={isLoading}
                fieldToFocus="displayOrder"
                ref={gridTableRef}
                gridApiRef={apiRef}
                validateAllRows={validateAllRows}
                transformRowOnUpdate={(row) => {
                    const fileAnswerTypeName = AnswerTypeVariableNamesList.find(
                        (f) => f.id === AnswerTypeVariable.File
                    )?.name;
                    if (row.answerType === fileAnswerTypeName) {
                        return { ...row, formType: '', formList: '' };
                    }
                    return row;
                }}
            />
        </div>
    );
});

export default DataTableEditor;
